import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Select, Button, Row, Col, Typography, message } from 'antd';

import {
  fetchImageRequests,
  updateImageRequest,
  removeImageRequest,
} from '../store/actions/imageRequests-actions';
import { imageRequestsActions } from '../store/slices/imageRequests-slice';
import { fetchVehicles } from '../store/actions/vehicles-actions';
import { fetchAdmins } from '../store/actions/admin-actions';
import { SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import ImageRequestsTable from '../components/ImageRequests/ImageRequestsTable';
import CompleteRequestModal from '../components/ImageRequests/CompleteRequestModal';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

const { Option } = Select;
const { Title } = Typography;

const ImageRequests = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { isLoading, items, count, searchParams, currentPage, perPage } =
    useSelector((state) => state.imageRequests);
  const vehiclesState = useSelector((state) => state.vehicle);
  const loggedInUser = useSelector((state) => state.auth.loggedInUser);
  const admins = useSelector((state) => state.admin.admins);

  const [completeOpen, setCompleteOpen] = useState(false);
  const [currentRequest, setCurrentRequest] = useState(null);
  const [completeForm] = Form.useForm();
  const [vehicleFilter, setVehicleFilter] = useState({
    vin: '',
    make: '',
    model: '',
  });
  const [vehiclePage, setVehiclePage] = useState(1);
  const [vehiclePerPage] = useState(8);
  const [selectedVehicleId, setSelectedVehicleId] = useState(null);

  useEffect(() => {
    moment.locale(i18n.language || 'en');
    const controller = new AbortController();
    const { signal } = controller;

    const options = {
      params: { orderBy: 'priority', orderDirection: 'ASC', ...searchParams },
      signal,
    };

    dispatch(
      fetchImageRequests({
        pagination: { page: currentPage, total: perPage },
        options,
      })
    );

    return () => controller.abort();
  }, [dispatch, searchParams, currentPage, perPage, i18n.language]);

  useEffect(() => {
    if (!completeOpen) return;
    const vinPrefill = (currentRequest?.vin || '').trim();
    if (!vinPrefill) return;
    const controller = new AbortController();
    const { signal } = controller;
    dispatch(
      fetchVehicles({
        pagination: { page: 1, total: vehiclePerPage },
        options: { params: { vin: vinPrefill }, signal },
      })
    );
    return () => controller.abort();
  }, [completeOpen, currentRequest?.vin, vehiclePerPage, dispatch]);

  const onVehicleFilterChange = (field, value) => {
    const next = { ...vehicleFilter, [field]: value };
    setVehicleFilter(next);
    setVehiclePage(1);
  };

  const onVehicleFiltersClear = () => {
    setVehicleFilter({ vin: '', make: '', model: '' });
    setVehiclePage(1);
  };

  const onVehicleFiltersSearch = () => {
    const params = {};
    if (vehicleFilter.vin?.trim()) params.vin = vehicleFilter.vin.trim();
    if (vehicleFilter.make?.trim()) params.make = vehicleFilter.make.trim();
    if (vehicleFilter.model?.trim()) params.model = vehicleFilter.model.trim();
    setVehiclePage(1);
    const controller = new AbortController();
    const { signal } = controller;
    dispatch(
      fetchVehicles({
        pagination: { page: 1, total: vehiclePerPage },
        options: { params, signal },
      })
    );
  };

  const onVehiclePageChange = (page) => {
    setVehiclePage(page);
    const params = {};
    if (vehicleFilter.vin?.trim()) params.vin = vehicleFilter.vin.trim();
    if (vehicleFilter.make?.trim()) params.make = vehicleFilter.make.trim();
    if (vehicleFilter.model?.trim()) params.model = vehicleFilter.model.trim();
    const controller = new AbortController();
    const { signal } = controller;
    dispatch(
      fetchVehicles({
        pagination: { page, total: vehiclePerPage },
        options: { params, signal },
      })
    );
  };

  useEffect(() => {
    if (
      loggedInUser?.role?.name === 'root'
    ) {
      dispatch(fetchAdmins());
    }
  }, [dispatch, loggedInUser?.role?.name]);

  const [form] = Form.useForm();

  const onSearch = (values) => {
    const params = Object.keys(values).reduce((acc, k) => {
      const v = values[k];
      if (v !== undefined && v !== '') acc[k] = v;
      return acc;
    }, {});
    const allowedOrderBy = ['createdAt', 'priority'];
    if (params.orderBy && !allowedOrderBy.includes(params.orderBy)) {
      delete params.orderBy;
    }
    if (params.orderDirection) {
      const dir = String(params.orderDirection).toUpperCase();
      if (dir === 'ASC' || dir === 'DESC') params.orderDirection = dir;
      else delete params.orderDirection;
    }
    dispatch(imageRequestsActions.setSearchParams(params));
    dispatch(imageRequestsActions.setCurrentPage(1));
  };

  const onReset = () => {
    form.resetFields();
    const defaults = { orderBy: 'priority', orderDirection: 'ASC' };
    form.setFieldsValue(defaults);
    dispatch(imageRequestsActions.setSearchParams(defaults));
    dispatch(imageRequestsActions.setCurrentPage(1));
  };

  const initialFormValues = useMemo(
    () => ({ orderBy: 'priority', orderDirection: 'ASC', ...searchParams }),
    [searchParams]
  );

  const onStart = async (record) => {
    const id = record.id;
    const me = loggedInUser?.id;

    dispatch(
      imageRequestsActions.patchItem({
        id,
        changes: {
          status: 'inProgress',
          assignedTo: me,
          assignedToUser: {
            id: me,
            email: loggedInUser?.email || undefined,
            firstName: loggedInUser?.firstName || undefined,
            lastName: loggedInUser?.lastName || undefined,
          },
        },
      })
    );
    try {
      const res = await dispatch(
        updateImageRequest({ id, body: { status: 'inProgress' } })
      );
      if (res.error) throw res.error;
      message.success(t('imageRequests.messages.startSuccess'));
    } catch (e) {
      dispatch(
        imageRequestsActions.patchItem({
          id,
          changes: {
            status: 'pending',
            assignedTo: null,
            assignedToUser: null,
          },
        })
      );
      message.error(t('imageRequests.messages.startError'));
    }
  };

  const openComplete = (record) => {
    setCurrentRequest(record);
    completeForm.resetFields();
    setSelectedVehicleId(null);
    const vinPrefill = (record?.vin || '').trim();
    setVehiclePage(1);
    setVehicleFilter({ vin: vinPrefill, make: '', model: '' });
    setCompleteOpen(true);
  };

  const doComplete = async () => {
    try {
      const values = await completeForm.validateFields();
      const body = {
        status: 'completed',
        upload: true,
        vehicleId: values.vehicleId,
      };
      const id = currentRequest.id;
      dispatch(
        imageRequestsActions.patchItem({
          id,
          changes: { status: 'completed', vehicleId: body.vehicleId },
        })
      );
      const res = await dispatch(updateImageRequest({ id, body }));
      if (res.error) throw res.error;
      setCompleteOpen(false);
      setCurrentRequest(null);
      message.success(t('imageRequests.messages.completeSuccess'));
    } catch (e) {
      if (currentRequest) {
        const id = currentRequest.id;
        dispatch(
          imageRequestsActions.patchItem({
            id,
            changes: { status: 'inProgress' },
          })
        );
      }
      message.error(t('imageRequests.messages.completeError'));
    }
  };

  const onDelete = async (record) => {
    const id = record.id || record._id;
    try {
      const res = await dispatch(removeImageRequest(id));
      if (res.error) throw res.error;
      message.success(t('imageRequests.messages.deleteSuccess'));
    } catch (e) {
      message.error(t('imageRequests.messages.deleteError'));
    }
  };

  return (
    <>
      <Row style={{ marginBottom: 16 }} align="middle" justify="space-between">
        <Col>
          <Title level={3} style={{ margin: 0 }}>
            {t('imageRequests.title')}
          </Title>
        </Col>
      </Row>
      <Form
        form={form}
        layout="vertical"
        onFinish={onSearch}
        initialValues={initialFormValues}
        style={{ marginBottom: '1rem', maxWidth: '900px' }}
        className="searchForm"
      >
        <Row gutter={[8, 0]}>
          <Col xs={24} sm={12} md={8}>
            <Form.Item name="status" label={t('imageRequests.filters.status')}>
              <Select allowClear placeholder={t('common.all')}>
                <Option value="pending">
                  {t('imageRequests.statusLabel.pending')}
                </Option>
                <Option value="inProgress">
                  {t('imageRequests.statusLabel.inProgress')}
                </Option>
                <Option value="completed">
                  {t('imageRequests.statusLabel.completed')}
                </Option>
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Form.Item
              name="orderBy"
              label={t('imageRequests.filters.orderBy')}
            >
              <Select
                allowClear
                placeholder={t('imageRequests.filters.orderBy')}
              >
                <Option value="createdAt">
                  {t('imageRequests.orderBy.createdAt')}
                </Option>
                <Option value="priority" default>
                  {t('imageRequests.orderBy.priority')}
                </Option>
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Form.Item
              name="orderDirection"
              label={t('imageRequests.filters.direction')}
            >
              <Select
                allowClear
                placeholder={t('imageRequests.filters.direction')}
              >
                <Option value="ASC">{t('imageRequests.direction.asc')}</Option>
                <Option value="DESC">
                  {t('imageRequests.direction.desc')}
                </Option>
              </Select>
            </Form.Item>
          </Col>
          {loggedInUser?.role?.name === 'root' && (
            <Col xs={24} sm={12} md={8}>
              <Form.Item
                name="assignedTo"
                label={t('imageRequests.filters.assignedTo')}
              >
                <Select
                  allowClear
                  placeholder={t('imageRequests.filters.selectAdmin')}
                  showSearch
                  optionFilterProp="label"
                  options={(admins || []).map((a) => ({
                    value: a.id,
                    label: `${a.firstName || ''} ${a.lastName || ''}`,
                  }))}
                />
              </Form.Item>
            </Col>
          )}
        </Row>
        <Row justify="end">
          <Col>
            <Button
              type="primary"
              shape="round"
              icon={<SearchOutlined />}
              htmlType="submit"
              style={{ marginRight: 8 }}
            >
              {t('common.search')}
            </Button>
            <Button
              type="default"
              shape="round"
              icon={<ReloadOutlined />}
              onClick={onReset}
            >
              {t('common.reset')}
            </Button>
          </Col>
        </Row>
      </Form>

      <ImageRequestsTable
        items={items}
        loading={isLoading}
        currentPage={currentPage}
        perPage={perPage}
        total={count || (Array.isArray(items) ? items.length : 0)}
        onPageChange={(page) =>
          dispatch(imageRequestsActions.setCurrentPage(page))
        }
        loggedInUser={loggedInUser}
        t={t}
        onStart={onStart}
        onOpenComplete={openComplete}
        onDelete={onDelete}
      />

      <CompleteRequestModal
        open={completeOpen}
        onCancel={() => setCompleteOpen(false)}
        onOk={doComplete}
        okDisabled={!selectedVehicleId}
        t={t}
        completeForm={completeForm}
        vehicleFilter={vehicleFilter}
        onVehicleFilterChange={onVehicleFilterChange}
        onVehicleFiltersClear={onVehicleFiltersClear}
        onVehicleFiltersSearch={onVehicleFiltersSearch}
        vehiclesState={vehiclesState}
        selectedVehicleId={selectedVehicleId}
        setSelectedVehicleId={setSelectedVehicleId}
        vehiclePage={vehiclePage}
        onVehiclePageChange={onVehiclePageChange}
        currentRequest={currentRequest}
      />
    </>
  );
};

export default ImageRequests;
